/**
 * Add-to-cart confirmation tooltip.
 *
 * Shown when the custom side cart is switched off: without the drawer sliding
 * open, an AJAX add is otherwise silent apart from the header badge ticking up.
 * Anchors a short "added" popover to whichever header bag is visible and offers
 * a way through to the cart page.
 *
 * Guarded on the markup: inc/cart-tooltip.php only renders it when the drawer is
 * off, so with the drawer on nothing below runs.
 */

const tooltips = Array.from(
  document.querySelectorAll<HTMLElement>(".niamito-cart-tooltip")
);

if (tooltips.length) {
  const $ = (window as any).jQuery;
  const VISIBLE_MS = 4000;

  // One timer across all instances: only ever one tooltip is on screen (the
  // other sits in the hidden breakpoint's header), so they share the schedule.
  let hideTimer: number | undefined;

  const clearTimer = (): void => {
    if (hideTimer !== undefined) {
      window.clearTimeout(hideTimer);
      hideTimer = undefined;
    }
  };

  const hideAll = (): void => {
    clearTimer();
    tooltips.forEach((el) => {
      el.classList.remove("is-open");
      el.hidden = true;
    });
  };

  const startTimer = (): void => {
    clearTimer();
    hideTimer = window.setTimeout(hideAll, VISIBLE_MS);
  };

  /**
   * Reveal every instance. The one in the hidden header is display:none via the
   * breakpoint's own rules, so showing both is harmless and saves us guessing
   * which header is live at the current width.
   *
   * `hidden` is dropped a frame before `is-open` goes on so the CSS transition
   * has a start state to animate from.
   */
  const show = (): void => {
    tooltips.forEach((el) => {
      el.hidden = false;
    });
    window.requestAnimationFrame(() => {
      tooltips.forEach((el) => el.classList.add("is-open"));
    });
    startTimer();
  };

  tooltips.forEach((el) => {
    // Hovering or tabbing in means the shopper is reaching for the CTA — the
    // tooltip must not evaporate mid-reach. Timer resumes when they leave.
    el.addEventListener("mouseenter", clearTimer);
    el.addEventListener("mouseleave", () => {
      if (!el.hidden) startTimer();
    });
    el.addEventListener("focusin", clearTimer);
    el.addEventListener("focusout", () => {
      if (!el.hidden) startTimer();
    });

    // Clicks inside must not reach the outside-click handler below.
    el.addEventListener("click", (e) => e.stopPropagation());
  });

  document.addEventListener("click", () => hideAll());

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") hideAll();
  });

  if ($) {
    // `added_to_cart` covers WooCommerce's own archive/loop AJAX. The single
    // product page fires the namespaced event instead (singleProductAddToCart.ts)
    // so it can add to the cart without WooCommerce's handler appending its
    // "View cart" link next to the button.
    $(document.body).on("added_to_cart niamito_added_to_cart", show);
  }
}
